import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useState, useEffect, useRef } from "react";

import colors from "@styles/variables.module.scss";

const ModelLoader = () => {
  const meshRef = useRef();

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.5;
      meshRef.current.rotation.y += delta * 0.2;
    }
  });

  return (
    <mesh ref={meshRef}>
      <dodecahedronGeometry args={[1, 0]} />
      <meshStandardMaterial
        color={colors.mainColor}
        metalness={0.8}
        roughness={0.2}
        wireframe={false}
        emissive={colors.mainColor}
        emissiveIntensity={0.2}
      />
    </mesh>
  );
};

const srcOld = "/static/models/porsche-911-light.glb";
const srcNew = "/static/models/SUPERIOR-bus.glb";

// Preload default model
// useGLTF.preload(srcNew);

const ModelContent = ({ url = "", onLoad, ...props }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const modelRef = useRef();
  const sceneRef = useRef(null);

  // Call useGLTF at the top level
  const { scene } = useGLTF(url);

  // Animation frame hook must be called unconditionally
  useFrame((state, delta) => {
    if (modelRef.current && !isLoading && sceneRef.current) {
      modelRef.current.rotation.y += delta * 0.15;
    }
  });

  // Optimize scene in a separate effect to prevent UI blocking
  useEffect(() => {
    if (!scene) return;

    const optimizeScene = async () => {
      try {
        const clonedScene = scene.clone();

        // Use requestAnimationFrame to break up the work
        const optimizeMesh = (mesh) => {
          return new Promise((resolve) => {
            requestAnimationFrame(() => {
              if (mesh.isMesh) {
                mesh.frustumCulled = true;
                if (mesh.geometry) {
                  mesh.geometry.computeBoundingSphere();
                  mesh.geometry.computeBoundingBox();
                }
              }
              resolve();
            });
          });
        };

        // Process meshes in chunks
        const meshes = [];
        clonedScene.traverse((child) => {
          if (child.isMesh) meshes.push(child);
        });

        // Process meshes in chunks of 5
        for (let i = 0; i < meshes.length; i += 5) {
          const chunk = meshes.slice(i, i + 5);
          await Promise.all(chunk.map(optimizeMesh));
        }

        console.log("[ThreeDModel] Scene optimization completed");
        sceneRef.current = clonedScene;
        setIsLoading(false);
        onLoad?.();
      } catch (err) {
        console.error("[ThreeDModel] Optimization error:", err);
        setError(err);
      }
    };

    optimizeScene();
  }, [scene, onLoad]);

  if (error) {
    console.error("[ThreeDModel] Model failed to load:", error);
    return <ModelLoader />;
  }

  if (isLoading || !sceneRef.current) {
    console.log("[ThreeDModel] Loading or optimizing scene");
    return <ModelLoader />;
  }

  return (
    <primitive
      key={url}
      ref={modelRef}
      object={sceneRef.current}
      dispose={null}
      {...props}
      scale={[0.5, 0.5, 0.5]}
      position={[0, -1, 0]}
      rotation={[0, 0, 0]}
    />
  );
};

export default ModelContent;
